<script lang="ts" setup>
import { trackEvent } from '@@/bits/analytics'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import { OzDropdownSizePreset } from '@@/library/v4/components/OzDropdown.vue'
import OzDropdownButton, {
  OzDropdownButtonColorScheme,
  OzDropdownButtonSizePreset,
} from '@@/library/v4/components/OzDropdownButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import OzInput, { OzInputColorScheme, OzInputSizePreset } from '@@/library/v4/components/OzInput.vue'
import { OzListRowSeparator } from '@@/library/v4/components/OzListRow.vue'
import OzLoadingText from '@@/library/v4/components/OzLoadingText.vue'
import OzPadletLogo, { OzPadletLogoColorScheme } from '@@/library/v4/components/OzPadletLogo.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzRowDropdown, { type OzRowDropdownRow } from '@@/library/v4/components/OzRowDropdown.vue'
import OzToggle from '@@/library/v4/components/OzToggle.vue'
import { useLibraryLtiStore } from '@@/pinia/library_lti_store'
import AccountRow from '@@/vuecomponents/AccountRow.vue'
import LtiWallListRow from '@@/vuecomponents/LtiWallListRow.vue'
import { useDocumentVisibility, useElementHover } from '@vueuse/core'
import { debounce } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, shallowRef, watch } from 'vue'

const libraryLtiStore = useLibraryLtiStore()
const {
  districtLibraries,
  newDistrictLibraryId,
  isAwaitingServerResponse,
  isDistrictDeployment,
  hasMultipleDistrictLibraries,
  currentLibraryId,
  isEdlinkFlow,
} = storeToRefs(libraryLtiStore)

// Compute the selected library's name
const selectedLibraryName = computed(() => {
  // First try to find by newDistrictLibraryId (user's current selection)
  if (newDistrictLibraryId.value && districtLibraries.value.length > 0) {
    const selectedLibrary = districtLibraries.value.find((lib) => lib.id === newDistrictLibraryId.value)
    if (selectedLibrary) return selectedLibrary.name
  }

  // Fall back to currentLibraryId if no selection made yet
  if (currentLibraryId.value && districtLibraries.value.length > 0) {
    const currentLibrary = districtLibraries.value.find((lib) => lib.id === currentLibraryId.value)
    if (currentLibrary) return currentLibrary.name
  }

  // Final fallback to the library name from the store
  return libraryLtiStore.libraryName
})

const showLibrarySelector = ref(false)
const deepLinkForm = ref<HTMLFormElement | null>(null)
const jwtMessageInput = ref<HTMLFormElement | null>(null)
const doRemake = ref(false)
const makeGradebook = ref(true)

// Function to handle library selection for district libraries
function onSelectLibrary(selectedLibraryId: number): void {
  // Set the selected library ID in store
  libraryLtiStore.setSelectedLibraryId(selectedLibraryId)

  // Close the library selector immediately (the launching will happen in background)
  showLibrarySelector.value = false

  // Save selected library to server and reload screen with new library
  libraryLtiStore.saveNewDistrictLibraryAndLaunchDeepLinkMenu(selectedLibraryId)
}

const deepLinkUrlSearchParams = computed((): Record<string, string> => {
  if (isEdlinkFlow.value) return {}
  const hash = {}
  new URL(libraryLtiStore.deepLinkUrl).searchParams.forEach((k, v) => {
    hash[k] = v
  })
  return hash
})

// #region Search & filter walls
const WALL_FILTER_DROPDOWN_ARIA_LABEL = __('Padlets filter dropdown')

const wallFilterDropdownRows = computed<OzRowDropdownRow<string, unknown>[]>(() => {
  return libraryLtiStore.wallFilterOptions.map((option) => ({
    icon: option.icon,
    rightIcon: libraryLtiStore.selectedWallFilter === option.value ? 'radio_selected' : 'radio_unselected',
    rightIconSize: 24,
    rightIconHighlighted: libraryLtiStore.selectedWallFilter === option.value,
    eventPayload: option.value,
    text: option.label,
    htmlText: `<p class="text-body-small text-dark-text-100 dark:text-light-text-100">${option.label}</p>`,
    testId: `libraryLtiWallFilter${option.value}`,
  }))
})

const activeWallFilterLabel = computed<string>(() => {
  return (
    wallFilterDropdownRows.value.find((row) => row.eventPayload === libraryLtiStore.selectedWallFilter)?.text ??
    __('Recents')
  )
})

function changeWallFilter(_: Event, selectedValue: string): void {
  handleClearSearchAndFilter()
  libraryLtiStore.filterWalls(selectedValue)
}

const searchInputRef = shallowRef<InstanceType<typeof OzInput> | null>(null)

const debouncedSearchWalls = debounce(
  (query: string) => {
    trackEvent('Library LTI Deeplink Menu', 'Searched padlets')
    libraryLtiStore.searchWalls(query)
  },
  500,
  {
    edges: ['trailing'],
  },
)

function handleSearchInputChange(event: Event): void {
  const target = event.target as HTMLInputElement
  const query = target.value

  // Update store immediately for UI reactivity
  libraryLtiStore.searchQuery = query

  if (query && query.trim().length > 0) {
    libraryLtiStore.isSearching = true
    libraryLtiStore.selectedWallFilter = libraryLtiStore.DEFAULT_FILTER
    debouncedSearchWalls(query)
  } else {
    debouncedSearchWalls.cancel()
    handleClearSearchAndFilter()
  }
}

function handleClearSearchAndFilter(): void {
  libraryLtiStore.resetSearch()
  libraryLtiStore.resetFilter()
}
// #endregion

function trackPadletSelection(): void {
  if (libraryLtiStore.isSearchActive) {
    trackEvent('Library LTI Deeplink Menu', 'Selected padlet from search')
  } else if (libraryLtiStore.isFilterActive) {
    const filterValue = libraryLtiStore.selectedWallFilter
    trackEvent('Library LTI Deeplink Menu', `Selected padlet from ${filterValue}`)
  } else {
    trackEvent('Library LTI Deeplink Menu', 'Selected padlet from recents')
  }
}

function selectWall(wall): void {
  trackPadletSelection()

  let message = doRemake.value ? wall?.embed_remake_message : wall?.embed_message
  if (makeGradebook.value) {
    message = doRemake.value ? wall?.embed_remake_grade_message : wall?.embed_grade_message
  }
  deepLinkSubmit(message as string)
}

function deepLinkSubmit(message: string): void {
  const form = deepLinkForm.value
  const jwtMessageInputRef = jwtMessageInput.value
  if (form == null || jwtMessageInputRef == null) return
  jwtMessageInputRef.value = message
  form.submit()
}

const wallListEl = ref<HTMLElement>()

// If the mouse is NOT hovered inside the wall list element, refresh the wall list every 3s
watch(
  useElementHover(wallListEl, { delayLeave: 1000 }),
  (isHovered) => {
    if (isEdlinkFlow.value) return

    if (isHovered) {
      libraryLtiStore.stopRefreshCurrentWallList()
    } else {
      libraryLtiStore.refreshCurrentWallList({ intervalMs: 3000 })
    }
  },
  { immediate: true },
)

// Whenever the document becomes visible again, refresh the wall list
// in case there were new padlets created while the user was away
watch(useDocumentVisibility(), (visibility) => {
  if (isEdlinkFlow.value) return

  if (visibility === 'visible') {
    libraryLtiStore.refreshCurrentWallList({ intervalMs: 3000 })
  } else {
    libraryLtiStore.stopRefreshCurrentWallList()
  }
})

onMounted(() => {
  setTimeout(() => {
    libraryLtiStore.setExpiryErrorScreen()
  }, libraryLtiStore.deepLinkTimeoutSeconds * 1000)
})
</script>

<template>
  <div class="flex flex-col p-3 phone:p-8">
    <div class="flex items-center justify-between mb-8">
      <div class="flex flex-col tablet-portrait:ms-4">
        <OzPadletLogo
          :color-scheme="OzPadletLogoColorScheme.Text300"
          :href="libraryLtiStore.openDashboardUrl"
          target="_blank"
          :class="['w-[66px] tablet-portrait:w-[100px] mb-1.5']"
        />
        <template v-if="isDistrictDeployment && hasMultipleDistrictLibraries">
          <OzDropdownButton
            class="!text-body-posts max-w-[200px] -ms-3"
            :text="selectedLibraryName"
            :size-preset="OzDropdownButtonSizePreset.H20px"
            :color-scheme="OzDropdownButtonColorScheme.TransparentSecondaryText"
            :should-truncate-text="true"
            :disabled="isAwaitingServerResponse"
            @click="showLibrarySelector = true"
          />

          <div v-if="isAwaitingServerResponse" class="mt-1 text-14-16 text-dark-text-200 dark:text-light-text-200">
            {{ __('Switching libraries...') }}
          </div>
        </template>
        <span v-else class="text-14-16 text-dark-text-200 dark:text-light-text-200">{{
          libraryLtiStore.libraryName
        }}</span>
      </div>

      <div class="flex flex-col items-end">
        <div class="flex items-center">
          <span class="text-17-22 text-dark-text-100 dark:text-light-text-100">
            {{ libraryLtiStore.userDisplayName }}
          </span>
          <OzAvatar
            :height="20"
            :width="20"
            class="hidden phone:inline-block rounded-full ms-2"
            :src="libraryLtiStore.avatar"
            :alt-text-name="libraryLtiStore.username"
          />
        </div>
        <span class="text-14-16 text-dark-text-200 dark:text-light-text-200 phone:me-7">
          {{ libraryLtiStore.username }}
        </span>
      </div>
    </div>

    <div
      v-if="showLibrarySelector && isDistrictDeployment"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      tabindex="0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="library-selector-dialog-title"
      @click="showLibrarySelector = false"
      @keydown.esc="showLibrarySelector = false"
    >
      <div class="bg-light-ui-100 dark:bg-dark-ui-100 rounded-2.5xl w-full max-w-md p-4" @click.stop>
        <h2
          id="library-selector-dialog-title"
          class="text-17-22 font-semibold text-dark-text-100 dark:text-light-text-100 mb-4"
        >
          {{ __('Select a School') }}
        </h2>
        <OzBox
          :color="OzBoxColors.Secondary"
          class="flex flex-col px-2 py-1 overflow-y-auto scrollbar-thin dark:scrollbar-thin-dark mb-4"
        >
          <ul role="listbox">
            <li v-for="(library, index) in districtLibraries" :key="library.id">
              <AccountRow
                :disabled="currentLibraryId === library.id"
                :avatar-url="library.avatarUrl"
                :title="library.name"
                :x-divider="index !== districtLibraries.length - 1"
                :selected="newDistrictLibraryId === library.id"
                right-icon="chevron_right_center"
                @click="onSelectLibrary(library.id)"
              />
            </li>
          </ul>
        </OzBox>
        <div class="flex justify-end">
          <OzLoadingText
            v-if="isAwaitingServerResponse"
            :text="__('Switching...')"
            class="text-heading-4 text-center font-semibold px-5 py-2"
          />
          <OzContainedButton
            v-else
            :color-scheme="OzContainedButtonColorScheme.Secondary"
            @click="showLibrarySelector = false"
          >
            {{ __('Cancel') }}
          </OzContainedButton>
        </div>
      </div>
    </div>

    <div class="flex justify-end tablet-portrait:justify-between mb-8">
      <OzContainedButton
        :href="libraryLtiStore.openDashboardUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Secondary"
        class="w-full phone:w-auto"
        data-testid="libraryLtiOpenDashboardButton"
        :aria-label="__('Open dashboard (opens in new tab)')"
        >{{ __('Open dashboard') }}
      </OzContainedButton>
      <OzContainedButton
        :href="libraryLtiStore.createNewPadletUrl"
        target="_blank"
        :color-scheme="OzContainedButtonColorScheme.Primary"
        class="w-full phone:w-auto ms-3"
        data-testid="libraryLtiCreateNewPadletButton"
        :aria-label="__('Make a padlet (opens in new tab)')"
        >{{ __('Make a padlet') }}
      </OzContainedButton>
    </div>

    <template v-if="libraryLtiStore.walls.length">
      <div class="flex items-center justify-between bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl px-4 py-3 mb-6">
        <div class="flex flex-col me-4">
          <h3 id="gradebook-toggle-label" class="text-17-22 text-dark-text-100 dark:text-light-text-100 mb-0.5">
            {{ __('Create a gradebook entry') }}
          </h3>
          <div class="text-14-16 text-dark-text-200 dark:text-light-text-200">
            <span class="hidden tablet-portrait:inline-block">{{
              __('Create a gradebook entry to sync grades to.')
            }}</span>
            <span>{{ __('Turn this off if you don’t want to sync grades to your LMS.') }}</span>
          </div>
        </div>
        <OzToggle
          class="shrink-0"
          :value="makeGradebook"
          aria-labelledby="gradebook-toggle-label"
          @input="makeGradebook = !makeGradebook"
        />
      </div>

      <div class="flex items-center justify-between bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl px-4 py-3 mb-6">
        <div class="flex flex-col me-4">
          <h3 id="remake-toggle-label" class="text-17-22 text-dark-text-100 dark:text-light-text-100 mb-0.5">
            {{ __('Create a copy for each user') }}
          </h3>
          <div class="text-14-16 text-dark-text-200 dark:text-light-text-200">
            <span class="hidden tablet-portrait:inline-block">{{ __('Use this padlet as a template.') }}</span>
            <span>{{ __('Turn this off if you want all users to work on the same padlet.') }}</span>
          </div>
        </div>

        <OzToggle
          class="shrink-0"
          :value="doRemake"
          aria-labelledby="remake-toggle-label"
          @input="doRemake = !doRemake"
        />
      </div>

      <div class="flex flex-col">
        <h3
          v-if="!isAppUsing('ltiDeepLinkingMenuV2')"
          class="text-17 text-dark-text-200 dark:text-light-text-200 ms-4 mb-1"
        >
          {{ __('Please select a padlet') }}
        </h3>
        <div v-else class="flex justify-between ms-4 h-8">
          <OzRowDropdown
            :x-dropdown-button="!libraryLtiStore.isSearchActive"
            :rows="wallFilterDropdownRows"
            :field-label="__('Please select a padlet')"
            :field-label-color-scheme="'text-200'"
            :dropdown-aria-label="WALL_FILTER_DROPDOWN_ARIA_LABEL"
            :dropdown-button-color-scheme="OzDropdownButtonColorScheme.SecondaryClear"
            :dropdown-size-preset="OzDropdownSizePreset.H52px"
            :button-text="activeWallFilterLabel"
            button-test-id="libraryLtiWallFilterDropdownButton"
            dropdown-test-id="libraryLtiWallFilterDropdown"
            class="!py-0"
            @select-row="changeWallFilter"
          >
            <template #fieldLabelEndAdornment>
              <span class="relative">
                <OzIndeterminateSpinner
                  v-if="libraryLtiStore.isSearchingOrFiltering"
                  class="absolute top-0 -end-8"
                  aria-live="polite"
                  :aria-label="__('Loading padlets')"
                />
                <OzIconButton
                  v-else
                  class="absolute -top-1.5 -end-9.5"
                  :label="__('Refresh the padlet list')"
                  :icon-name="'refresh'"
                  :icon-size="20"
                  :size-preset="OzIconButtonSizePreset.H32px"
                  :color-scheme="OzIconButtonColorScheme.SecondaryClear"
                  @click="libraryLtiStore.refreshCurrentWallList"
                />
              </span>
            </template>
          </OzRowDropdown>
        </div>
        <div v-if="isAppUsing('ltiDeepLinkingMenuV2')" class="mt-1 mb-3">
          <OzInput
            ref="searchInputRef"
            type="search"
            name="search"
            autocomplete="off"
            :value="libraryLtiStore.searchQuery"
            :placeholder="__('Search padlets')"
            :aria-label="__('Search padlets by keywords')"
            :color-scheme="OzInputColorScheme.Secondary200"
            :size-preset="OzInputSizePreset.H32px"
            test-id="libraryLtiDeeplinkMenuScreenSearchInput"
            @input="handleSearchInputChange"
          >
            <template #startAdornment>
              <div class="flex items-center justify-center ms-2.5 me-2">
                <OzIcon name="search" :size="20" class="text-dark-text-200 dark:text-light-text-200" />
              </div>
            </template>
            <template #endAdornment>
              <div class="flex mx-1">
                <OzPlainButton
                  v-if="libraryLtiStore.isSearchActive"
                  :class="[
                    'text-dark-text-300 dark:text-light-text-300',
                    'hover-hover:hover:text-dark-text-200 hover-hover:dark:hover:text-light-text-200',
                  ]"
                  :aria-label="__('Clear search keywords')"
                  :size-preset="OzPlainButtonSizePreset.H28px"
                  :color-scheme="OzPlainButtonColorScheme.Bare"
                  @click="handleClearSearchAndFilter"
                >
                  <template #icon>
                    <OzIcon name="clear_filled" :size="20" />
                  </template>
                </OzPlainButton>
              </div>
            </template>
          </OzInput>
        </div>

        <!-- Notice island -->
        <div
          v-if="
            isAppUsing('ltiDeepLinkingMenuV2') &&
            (libraryLtiStore.hasSearchOrFilterError ||
              (libraryLtiStore.displayedWalls.length === 0 && !libraryLtiStore.isSearchingOrFiltering))
          "
          class="bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl mb-3 px-4 py-2"
        >
          <p v-if="libraryLtiStore.hasSearchOrFilterError" class="py-2 text-danger-100">
            {{ __('There was an error while searching. Please try again.') }}
          </p>
          <p
            v-else-if="libraryLtiStore.displayedWalls.length === 0 && !libraryLtiStore.isSearchingOrFiltering"
            class="py-2"
          >
            {{ __('No padlets found.') }}
          </p>
        </div>

        <!-- Results island -->
        <div
          v-if="libraryLtiStore.displayedWalls.length > 0 && !libraryLtiStore.hasSearchOrFilterError"
          ref="wallListEl"
          class="flex flex-col bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl p-2"
        >
          <LtiWallListRow
            v-for="(wall, i) in libraryLtiStore.displayedWalls"
            :key="wall.id"
            :wall="wall"
            :separator="
              i < libraryLtiStore.displayedWalls.length - 1 ? OzListRowSeparator.Short : OzListRowSeparator.None
            "
            @select="selectWall"
          />
        </div>
      </div>
    </template>
    <div v-else class="flex flex-col bg-light-ui-200 dark:bg-dark-ui-200 rounded-2.5xl p-4">
      <p class="text-17-22 text-dark-text-100 dark:text-light-text-100">
        {{ __('No padlets available. Please create one.') }}
      </p>
    </div>

    <form ref="deepLinkForm" :action="libraryLtiStore.deepLinkUrl" method="POST">
      <input v-for="(k, v) of deepLinkUrlSearchParams" :key="k" type="hidden" :name="k" :value="v" />
      <input ref="jwtMessageInput" type="hidden" name="JWT" />
    </form>
  </div>
</template>
